import { getGasStorageChart } from "$/api/market-monitoring/fundamentals.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import { RadioButtonSet } from "$/lib/components/molecules/RadioButtonSet";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

// const YEARS_OPTIONS = Array.from(new Array(6), (_, i) =>
//   i === 0
//     ? { value: "all", label: "Tous" }
//     : {
//         value: `${2020 + i}`,
//         label: `${2020 + i}`,
//       },
// );

const FR_GAS_COLORS = [
  "#eed36e",
  "#e69440",
  "#cc5d2f",
  "#933d2f",
  "#6b2d2f",
  "#4b2d2f",
  "#2f2d2f",
];

const EU_GAS_COLORS = [
  "#c2e5d8",
  "#e0ebec",
  "#d6e4e9",
  "#d2dee4",
  "#d1d7df",
  "#cfdfe2",
  "#cde7e5",
];

const GasStorageChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [filters, setFilters] = useState<{
    year?: string;
    locale: "all" | "FR" | "EU";
  }>({ locale: "FR" });

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-storage", filters.year],
    queryFn: () =>
      getGasStorageChart({
        year: filters.year === "all" ? undefined : filters.year,
      }),
  });

  const last3Years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(new Array(3), (_, i) => currentYear - i);
  }, []);

  const toDisplay = useMemo(
    () =>
      data
        ? filters.locale === "all"
          ? data.mergedData
          : filters.locale === "FR"
            ? data.frData
            : filters.locale === "EU"
              ? data.euData
              : []
        : [],
    [data, filters.locale],
  );

  const FR_keys = useMemo(() => getArrayKeys(data?.frData ?? []), [data]);
  const EU_keys = useMemo(() => getArrayKeys(data?.euData ?? []), [data]);

  const keys = useMemo(
    () =>
      filters.locale === "all"
        ? [...FR_keys, ...EU_keys]
        : filters.locale === "FR"
          ? FR_keys
          : EU_keys,
    [FR_keys, EU_keys, filters.locale],
  );

  const COLORS = useMemo(
    () =>
      filters.locale === "all"
        ? [
            ...FR_GAS_COLORS.slice(0, FR_keys.length),
            ...EU_GAS_COLORS.slice(0, EU_keys.length),
          ]
        : filters.locale === "FR"
          ? FR_GAS_COLORS
          : filters.locale === "EU"
            ? EU_GAS_COLORS
            : [],
    [EU_keys.length, FR_keys.length, filters.locale],
  );

  const colors: Record<string, string> = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]: COLORS[i],
        }),
        {},
      ),
    [keys, COLORS],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    setGraphVisibility(
      keys.map((key) => ({
        name: key,
        isVisible: Boolean(
          last3Years.find((year) => key.includes(String(year))),
        ),
        color: colors[key],
      })),
    );
  }, [keys, filters, colors, last3Years]);

  return (
    <DisplayBox
      title="Taux de remplissage des stocks de gaz français et européens"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      Icon={LineChartIcon}
      theme="orange"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="!h-[510px] py-0">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <LineGraph
            data={toDisplay}
            xAxisDataKey="date"
            colors={colors}
            unit=" %"
            theme="orange"
            keys={keys}
            domain={[0, 100]}
            leftMargin={-30}
            hasCustomXAxisDomain={false}
            hasReferences={false}
            graphVisibility={graphVisibility}
          />
        )}

        <FiltersDisplayBox>
          {/* <Dropdown<string>
            options={YEARS_OPTIONS}
            placeholder="Année"
            onChange={(option) => {
              if (!option) return;
              setFilters((prev) => ({ ...prev, year: option.value }));
            }}
            value={filters.year}
            containerClassName="w-[150px]"
          /> */}

          <RadioButtonSet<"all" | "FR" | "EU">
            id="locale"
            title="Maille géographique"
            selectedId={filters.locale}
            onChange={(id) => setFilters((prev) => ({ ...prev, locale: id }))}
            options={[
              { id: "all", label: "(Tout)" },
              { id: "FR", label: "France" },
              { id: "EU", label: "EU" },
            ]}
          />

          <Flexbox className="gap-4 overflow-y-auto pb-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasStorageChart;
